import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { PunchupFieldLabel } from '../PunchupFieldLabel';
import { seedBodyHtml } from './seedBodyHtml';
import styles from './PunchupRichTextField.module.css';

export interface PunchupRichTextFieldProps {
	/** react-hook-form field path. */
	name: string;
	id?: string;
	label?: string;
	required?: boolean;
}

/** The body-HTML vocabulary Punchup's renderer keeps (h2–h4, not h1). Strike,
 *  background and alignment are left out: the renderer strips them. */
const TOOLBAR = [
	[{ header: [2, 3, 4, false] }],
	['bold', 'italic', 'underline', 'blockquote'],
	[{ color: [] }],
	[{ list: 'ordered' }, { list: 'bullet' }],
	['link', 'clean'],
];

const FORMATS = ['header', 'bold', 'italic', 'underline', 'blockquote', 'color', 'list', 'bullet', 'link'];

/**
 * PunchupRichTextField
 *
 * Rich-text (react-quill) form field bound to react-hook-form by `name`. The
 * value is body HTML. Only a user edit writes the form: Quill normalizes the
 * value on mount, and that must neither dirty the form nor rewrite a legacy
 * plain-text value on an unrelated save. The legacy value is seeded for display
 * only, so its line breaks show.
 */
const PunchupRichTextField = ({ name, id, label, required }: PunchupRichTextFieldProps) => {
	const {
		setValue,
		watch,
		formState: { errors, dirtyFields },
	} = useFormContext();
	const error = errors[name];
	const isFieldDirty = !!dirtyFields[name];
	const value: string = watch(name) || '';

	const modules = useMemo(() => ({ toolbar: TOOLBAR, clipboard: { matchVisual: false } }), []);
	const editorId = id ?? name;
	// Quill shifts its link tooltip back inside these bounds, which default to the
	// page. An attribute selector, since a field name can hold dots.
	const bounds = `[id="${editorId}"]`;

	return (
		<div className={styles.container}>
			{/* A span, not <label for>: the editor is a contenteditable, which a
			    label cannot target; the group below carries the name. */}
			{label && <PunchupFieldLabel as="span" label={label} required={required} />}
			<div
				role="group"
				aria-label={label ?? name}
				className={[styles.editor, error ? styles.editorError : isFieldDirty ? styles.editorDirty : null]
					.filter(Boolean)
					.join(' ')}
			>
				<ReactQuill
					id={editorId}
					theme="snow"
					bounds={bounds}
					value={seedBodyHtml(value)}
					modules={modules}
					formats={FORMATS}
					onChange={(html, _delta, source) => {
						if (source === 'user') setValue(name, html, { shouldDirty: true });
					}}
				/>
			</div>
			{error?.message && <span className="punchupFormError">{error.message as string}</span>}
		</div>
	);
};

export { PunchupRichTextField };
